import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Alert,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import GlobalIcon from "../GlobalIcon/GlobalIcon";
import {
  searchBrokerInstruments,
  addWatchlistInstrument,
  removeWatchlistInstrument,
} from "../../api/watchlist";
import {
  DEFAULT_INSTRUMENT_SEARCH_QUERY,
  MIN_INSTRUMENT_SEARCH_LENGTH,
  mapInstrumentSearchResults,
} from "./instrumentSearchData";
import styles from "./AddWatchlistSymbolModal.module.css";

const SEARCH_DEBOUNCE_MS = 300;
const TOO_SHORT_QUERY = "__TOO_SHORT__";

const getSymbolKey = (value) => String(value || "").split("/")[0].trim().toUpperCase();

function AddWatchlistSymbolModal({ open, onClose, existingSymbolKeys = new Set(), onAdded, onRemoved }) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState(DEFAULT_INSTRUMENT_SEARCH_QUERY);
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [addedIds, setAddedIds] = useState(() => new Set());
  const [removedKeys, setRemovedKeys] = useState(() => new Set());

  const listRef = useRef(null);
  const requestIdRef = useRef(0);

  const existingKeys = useMemo(() => {
    const keys = new Set();
    existingSymbolKeys.forEach((key) => {
      if (key) keys.add(String(key).toUpperCase());
    });
    return keys;
  }, [existingSymbolKeys]);

  const isQueryTooShort = debouncedQuery === TOO_SHORT_QUERY;

  const resetState = useCallback(() => {
    setQuery("");
    setDebouncedQuery(DEFAULT_INSTRUMENT_SEARCH_QUERY);
    setResults([]);
    setError("");
    setBusyId(null);
    setAddedIds(new Set());
    setRemovedKeys(new Set());
  }, []);

  const closeModal = useCallback(() => {
    if (busyId) return;
    resetState();
    onClose();
  }, [busyId, onClose, resetState]);

  useEffect(() => {
    if (!open) return undefined;

    setQuery("");
    setDebouncedQuery(DEFAULT_INSTRUMENT_SEARCH_QUERY);

    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const trimmed = query.trim();

    if (trimmed.length === 0) {
      const timer = setTimeout(() => setDebouncedQuery(DEFAULT_INSTRUMENT_SEARCH_QUERY), SEARCH_DEBOUNCE_MS);
      return () => clearTimeout(timer);
    }

    if (trimmed.length < MIN_INSTRUMENT_SEARCH_LENGTH) {
      const timer = setTimeout(() => setDebouncedQuery(TOO_SHORT_QUERY), SEARCH_DEBOUNCE_MS);
      return () => clearTimeout(timer);
    }

    const timer = setTimeout(() => setDebouncedQuery(trimmed), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, open]);

  const fetchResults = useCallback(async (searchQuery) => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);

    try {
      const response = await searchBrokerInstruments(searchQuery);
      if (requestId !== requestIdRef.current) return;

      setResults(mapInstrumentSearchResults(response));
      setError("");
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        "Unable to load symbols. Please try again.";
      setError(message);
      setResults([]);
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!open) return;

    if (isQueryTooShort) {
      setResults([]);
      setIsLoading(false);
      setError("");
      return;
    }

    if (!debouncedQuery) return;
    fetchResults(debouncedQuery);
  }, [open, debouncedQuery, isQueryTooShort, fetchResults]);

  const isInWatchlist = useCallback(
    (instrument) => {
      const key = getSymbolKey(instrument.symbol);
      if (removedKeys.has(key)) return false;
      return existingKeys.has(key) || addedIds.has(instrument.id);
    },
    [addedIds, existingKeys, removedKeys]
  );

  const handleAdd = useCallback(
    async (instrument) => {
      if (isInWatchlist(instrument) || busyId) return;

      setBusyId(instrument.id);
      setError("");

      try {
        await addWatchlistInstrument([
          {
            exchange_trading_symbol: instrument.exchangeTradingSymbol || instrument.symbol,
            exchange: instrument.exchange,
          },
        ]);

        const key = getSymbolKey(instrument.symbol);
        setRemovedKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
        setAddedIds((prev) => new Set(prev).add(instrument.id));
        onAdded?.(instrument);
      } catch (err) {
        const message =
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to add symbol to watchlist.";
        setError(message);
      } finally {
        setBusyId(null);
      }
    },
    [busyId, isInWatchlist, onAdded]
  );

  const handleRemove = useCallback(
    async (instrument) => {
      if (!isInWatchlist(instrument) || busyId) return;

      setBusyId(instrument.id);
      setError("");

      try {
        await removeWatchlistInstrument([
          {
            exchange_trading_symbol: instrument.exchangeTradingSymbol || instrument.symbol,
            exchange: instrument.exchange,
          },
        ]);

        const key = getSymbolKey(instrument.symbol);
        setRemovedKeys((prev) => new Set(prev).add(key));
        setAddedIds((prev) => {
          const next = new Set(prev);
          next.delete(instrument.id);
          return next;
        });
        onRemoved?.(instrument);
      } catch (err) {
        const message =
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to remove symbol from watchlist.";
        setError(message);
      } finally {
        setBusyId(null);
      }
    },
    [busyId, isInWatchlist, onRemoved]
  );

  const visibleCountLabel =
    results.length > 0 ? `${results.length} symbol${results.length === 1 ? "" : "s"} found` : "No symbols found";

  return (
    <Dialog
      open={open}
      onClose={busyId ? undefined : closeModal}
      maxWidth={false}
      scroll="paper"
      aria-labelledby="add-watchlist-symbol-title"
      PaperProps={{ className: styles.paper }}
      slotProps={{
        root: {
          sx: {
            "& .MuiDialog-container": {
              alignItems: "center",
              justifyContent: "center",
            },
          },
        },
        backdrop: {
          sx: {
            backgroundColor: "rgba(15, 23, 42, 0.48)",
            backdropFilter: "blur(6px)",
          },
        },
      }}
    >
      <div className={styles.titleRoot}>
        <div className={styles.header}>
          <div className={styles.headerMain}>
            <span className={styles.eyebrow}>Watchlist</span>
            <h2 id="add-watchlist-symbol-title" className={styles.heading}>
              Add symbol
            </h2>
            <p className={styles.subtitle}>Search instruments and add them to your watchlist.</p>
          </div>
          <IconButton
            type="button"
            onClick={closeModal}
            disabled={Boolean(busyId)}
            className={styles.closeBtn}
            aria-label="Close"
            size="small"
          >
            <GlobalIcon iconName="RoundedCrossIcon" style={{ width: 16, height: 16 }} />
          </IconButton>
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.searchWrap}>
          <TextField
            className={styles.searchField}
            placeholder="Search symbol (min 3 characters)…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            fullWidth
            size="small"
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <GlobalIcon iconName="SearchIcon" style={{ width: 18, height: 18, opacity: 0.45 }} />
                </InputAdornment>
              ),
            }}
          />
        </div>

        {error ? (
          <Alert severity="error" className={styles.error} onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}

        <div className={styles.listShell}>
          <div className={styles.listHeader}>
            <span>Symbol</span>
            <span>Exchange</span>
            <span aria-hidden="true" />
          </div>

          <div className={styles.list} ref={listRef}>
            {isLoading && results.length === 0 ? (
              <div className={styles.listFooter}>
                <CircularProgress size={18} sx={{ color: "#5446eb" }} />
                <span>Loading symbols…</span>
              </div>
            ) : isQueryTooShort ? (
              <div className={styles.empty}>
                <p className={styles.emptyTitle}>Keep typing</p>
                <p className={styles.emptyText}>Enter at least 3 characters to search instruments.</p>
              </div>
            ) : results.length === 0 ? (
              <div className={styles.empty}>
                <p className={styles.emptyTitle}>No matches</p>
                <p className={styles.emptyText}>Try a different symbol name or exchange.</p>
              </div>
            ) : (
              results.map((instrument) => {
                const alreadyAdded = isInWatchlist(instrument);
                const isBusy = busyId === instrument.id;

                return (
                  <div
                    key={instrument.id}
                    className={`${styles.row} ${alreadyAdded ? styles.rowInList : ""}`}
                  >
                    <div className={styles.rowSymbolBlock}>
                      <span className={styles.symbol}>{instrument.symbol}</span>
                      <span className={styles.description}>{instrument.description}</span>
                    </div>
                    <span className={styles.exchangeBadge}>{instrument.exchange}</span>
                    {alreadyAdded ? (
                      <button
                        type="button"
                        className={`${styles.actionBtn} ${styles.deleteBtn}`}
                        onClick={() => handleRemove(instrument)}
                        disabled={Boolean(busyId)}
                        title="Remove from watchlist"
                        aria-label={`Remove ${instrument.symbol} from watchlist`}
                      >
                        {isBusy ? (
                          <CircularProgress size={14} sx={{ color: "inherit" }} />
                        ) : (
                          <GlobalIcon iconName="BinIcon" style={{ width: 14, height: 14 }} />
                        )}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={`${styles.actionBtn} ${styles.addBtn}`}
                        onClick={() => handleAdd(instrument)}
                        disabled={Boolean(busyId)}
                        title="Add to watchlist"
                        aria-label={`Add ${instrument.symbol} to watchlist`}
                      >
                        {isBusy ? (
                          <CircularProgress size={14} sx={{ color: "inherit" }} />
                        ) : (
                          <GlobalIcon iconName="AddIcon" style={{ width: 14, height: 14 }} />
                        )}
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {results.length > 0 && (
            <div className={styles.listFooter}>
              <span>{visibleCountLabel}</span>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
}

export default AddWatchlistSymbolModal;
